import { createFileRoute, useNavigate } from "@tanstack/react-router";
import Building1 from "@repo/icons/default/Building1";
import CashDuplicate from "@repo/icons/default/CashDuplicate";
import Chart from "@repo/icons/default/Chart";
import CreditCard from "@repo/icons/default/CreditCard";
import FemaleDoctorWithSmartphone from "@repo/icons/default/FemaleDoctorWithSmartphone";
import Left from "@repo/icons/default/Left";
import { useState, type JSX } from "react";
import {
  HeaderButton,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { Page } from "@/components/ui/layout/page/page";
import {
  EmptyStateRoot,
  EmptyStateTitle,
  EmptyStateDescription,
  EmptyStateBody,
  EmptyStateButton,
} from "@/components/ui/empty-state/empty-state";
import { usePayAccountCreationState } from "@/hooks/use-pay-account-creation-state";
import { usePayFirstEntry } from "@/hooks/use-pay-first-entry";
import { usePayWelcomeCard } from "@/hooks/use-pay-welcome-card";

const PayWelcomePage = (): JSX.Element => {
  const navigate = useNavigate();
  const [isNavigating, setIsNavigating] = useState(false);

  const { clearCreationCompleted } = usePayAccountCreationState();
  const { markFirstEntry } = usePayFirstEntry();
  const { recipientId } = usePayWelcomeCard();

  const handleStartClick = (): void => {
    if (isNavigating) return;

    setIsNavigating(true);

    clearCreationCompleted();

    if (recipientId) {
      markFirstEntry(recipientId);
    }

    void navigate({
      to: "/settings/payments",
      search: { from: "welcome" },
    });
  };

  return (
    <>
      <HeaderRoot>
        <HeaderButton icon={Left} align="start" />
        <HeaderTitle title="Livance Pay" align="center" />
      </HeaderRoot>

      <Page>
        <EmptyStateRoot>
          <FemaleDoctorWithSmartphone size={124} className="w-[240px] h-[240px]" />
          <EmptyStateTitle>
            Seja bem-vindo ao <strong className="font-semibold">Livance Pay</strong>
          </EmptyStateTitle>

          <EmptyStateDescription>
            Nossa solução de pagamentos permite cobrar seus pacientes de forma simples e
            digital.
          </EmptyStateDescription>

          <EmptyStateBody>
            <ul className="flex flex-col gap-4 px-4 py-6 text-sm font-normal text-neutral-600">
              <li className="flex gap-3 items-start">
                <Building1 className="fill-primary-400 shrink-0" size={24} />
                <span>Cobre atendimentos realizados dentro e fora da Livance</span>
              </li>
              <li className="flex gap-3 items-start">
                <CreditCard className="fill-primary-400 shrink-0" size={24} />
                <span>
                  Pagamentos diretamente no totem das unidades ou através de link digital
                </span>
              </li>
              <li className="flex gap-3 items-start">
                <CashDuplicate className="fill-primary-400 shrink-0" size={24} />
                <span>Opções de Pix, crédito e débito</span>
              </li>
              <li className="flex gap-3 items-start">
                <Chart className="fill-primary-400 shrink-0" size={24} />
                <span>Visão consolidada para sua gestão financeira</span>
              </li>
            </ul>
          </EmptyStateBody>

          <EmptyStateButton fullWidth onClick={handleStartClick} loading={isNavigating}>
            Começar
          </EmptyStateButton>
        </EmptyStateRoot>
      </Page>
    </>
  );
};

export const Route = createFileRoute("/settings/payments/welcome")({
  component: PayWelcomePage,
});
